import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import PasswordInput from '../components/PasswordInput';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import { registerPasskey, passkeysSupported } from '../passkey';
import './Auth.css';
import './Account.css';

const MIN_PASSWORD = 12;

function formatDate(ts) {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return null; }
}

function Account() {
  const { user, loading, updateProfile, deleteAccount } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState(user?.full_name || '');
  const [salutationName, setSalutationName] = useState(user?.salutation_name || '');
  const [nameStatus, setNameStatus] = useState('');
  const [nameError, setNameError] = useState('');
  const [nameBusy, setNameBusy] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwStatus, setPwStatus] = useState('');
  const [pwBusy, setPwBusy] = useState(false);

  const [passkeys, setPasskeys] = useState([]);
  const [pkStatus, setPkStatus] = useState('');
  const [pkError, setPkError] = useState('');
  const [pkBusy, setPkBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    document.title = 'My account - Brookline Notepad';
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      setFullName(user.full_name || '');
      setSalutationName(user.salutation_name || '');
    }
  }, [user]);

  const loadPasskeys = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/passkeys');
      if (res.ok) setPasskeys(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadPasskeys(); }, [loadPasskeys]);

  if (!user) return null;

  const saveName = async (e) => {
    e.preventDefault();
    setNameError('');
    setNameStatus('');
    if (!fullName.trim()) { setNameError('Your name cannot be empty.'); return; }
    setNameBusy(true);
    try {
      await updateProfile(fullName.trim(), salutationName.trim());
      setNameStatus('Name updated.');
    } catch (err) {
      setNameError(err.message);
    } finally {
      setNameBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwStatus('');
    if (newPassword !== confirmPassword) { setPwError('New passwords do not match.'); return; }
    if (newPassword.length < MIN_PASSWORD) { setPwError(`Password must be at least ${MIN_PASSWORD} characters.`); return; }
    setPwBusy(true);
    try {
      const res = await fetch('/api/auth/change-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) { setPwError(data.error || 'Failed to change password.'); }
      else {
        setPwStatus('Password updated.');
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      }
    } catch {
      setPwError('Network error. Please try again.');
    } finally {
      setPwBusy(false);
    }
  };

  const addPasskey = async () => {
    setPkError('');
    setPkStatus('');
    setPkBusy(true);
    try {
      await registerPasskey('');
      setPkStatus('Passkey added.');
      await loadPasskeys();
    } catch (err) {
      // A user cancelling the OS prompt lands here too.
      setPkError(err.message || 'Could not add passkey.');
    } finally {
      setPkBusy(false);
    }
  };

  const confirmRemove = async () => {
    const pk = pendingDelete;
    setPendingDelete(null);
    setPkError('');
    setPkStatus('');
    try {
      const res = await fetch(`/api/auth/passkeys/${pk.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) { setPkError(data.error || 'Could not remove passkey.'); }
      else { setPkStatus('Passkey removed.'); await loadPasskeys(); }
    } catch {
      setPkError('Network error. Please try again.');
    }
  };

  const handleDeleteAccount = async () => {
    setConfirmDeleteAccount(false);
    setDeleteError('');
    try {
      await deleteAccount();
      navigate('/');
    } catch (err) {
      setDeleteError(err.message);
    }
  };

  return (
    <div className="auth-page">
      <a href="#main-content" className="auth-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" />

      <main id="main-content" tabIndex={-1} className="account-content">
        <h2 className="account-page-title">My account</h2>
        <p className="account-email">Signed in as {user.email}</p>

        {/* Name */}
        <section className="account-section" aria-labelledby="acct-name-h">
          <h3 id="acct-name-h">Your name</h3>
          <form onSubmit={saveName} className="auth-form" noValidate>
            <div className="auth-field">
              <label htmlFor="acct-full-name">Your Name</label>
              <input
                id="acct-full-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
                required
              />
            </div>
            <div className="auth-field">
              <label htmlFor="acct-salutation">I&rsquo;ll call you&hellip;</label>
              <input
                id="acct-salutation"
                type="text"
                value={salutationName}
                onChange={(e) => setSalutationName(e.target.value)}
                autoComplete="given-name"
              />
              <p className="account-hint">The name the site greets you by. Leave blank to use your full name.</p>
            </div>
            {nameError && <div className="auth-error" role="alert">{nameError}</div>}
            <button type="submit" className="auth-btn" disabled={nameBusy}>
              {nameBusy ? 'Saving…' : 'Save name'}
            </button>
          </form>
          <p className="auth-status" role="status" aria-live="polite">{nameStatus}</p>
        </section>

        {/* Password */}
        <section className="account-section" aria-labelledby="acct-pw-h">
          <h3 id="acct-pw-h">Change password</h3>
          <form onSubmit={changePassword} className="auth-form" noValidate>
            <PasswordInput
              id="acct-current" label="Current password" value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password"
            />
            <PasswordInput
              id="acct-new" label="New password" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password"
              minLength={MIN_PASSWORD} describedBy="acct-new-help"
            />
            <p id="acct-new-help" className="auth-help">At least {MIN_PASSWORD} characters.</p>
            <PasswordInput
              id="acct-confirm" label="Confirm new password" value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password"
              minLength={MIN_PASSWORD}
            />
            {pwError && <div className="auth-error" role="alert">{pwError}</div>}
            <button type="submit" className="auth-btn" disabled={pwBusy}>
              {pwBusy ? 'Updating…' : 'Update password'}
            </button>
          </form>
          <p className="auth-status" role="status" aria-live="polite">{pwStatus}</p>
        </section>

        {/* Passkeys */}
        <section className="account-section" aria-labelledby="acct-pk-h">
          <h3 id="acct-pk-h">Passkeys</h3>
          <p className="account-hint">
            Sign in with Touch ID, Face ID, Windows Hello, or a security key — no
            password to type.
          </p>

          {passkeys.length === 0 ? (
            <p className="account-hint">You haven’t added any passkeys yet.</p>
          ) : (
            <ul className="passkey-list">
              {passkeys.map((pk) => {
                const added = formatDate(pk.created_at);
                const used = formatDate(pk.last_used_at);
                return (
                  <li key={pk.id} className="passkey-item">
                    <div>
                      <span className="passkey-label">{pk.device_label || 'Passkey'}</span>
                      <span className="passkey-meta">
                        {added ? `Added ${added}` : null}
                        {used ? ` · Last used ${used}` : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="passkey-remove"
                      onClick={() => setPendingDelete(pk)}
                      aria-label={`Remove passkey ${pk.device_label || 'Passkey'}`}
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {passkeysSupported() ? (
            <button type="button" className="auth-btn account-inline-btn" onClick={addPasskey} disabled={pkBusy}>
              {pkBusy ? 'Waiting for your device…' : 'Add a passkey'}
            </button>
          ) : (
            <p className="account-hint">This browser doesn’t support passkeys.</p>
          )}
          {pkError && <div className="auth-error" role="alert">{pkError}</div>}
          <p className="auth-status" role="status" aria-live="polite">{pkStatus}</p>
        </section>

        {/* Delete account */}
        <section className="account-section account-danger" aria-labelledby="acct-delete-h">
          <h3 id="acct-delete-h">Delete my account</h3>
          <p className="account-hint">
            Permanently remove your Brookline Notepad account, including your
            access and passkeys. This cannot be undone.
          </p>
          {deleteError && <div className="auth-error" role="alert">{deleteError}</div>}
          <button
            type="button"
            className="auth-btn account-delete-btn"
            onClick={() => { setDeleteError(''); setConfirmDeleteAccount(true); }}
          >
            Delete My Account
          </button>
        </section>

        {user.is_admin && (
          <button className="auth-link-btn" onClick={() => navigate('/admin')}>
            Go to the admin console →
          </button>
        )}
        <button className="auth-link-btn" onClick={() => navigate('/')}>
          ← Back to Home
        </button>
      </main>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Remove this passkey?"
        message={`"${pendingDelete?.device_label || 'Passkey'}" will no longer be able to sign in to your account.`}
        confirmLabel="Remove"
        destructive
        onConfirm={confirmRemove}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={confirmDeleteAccount}
        title="Delete account"
        message="Are you sure you want to delete your Brookline Notepad account?"
        confirmLabel="Delete Account"
        destructive
        onConfirm={handleDeleteAccount}
        onCancel={() => setConfirmDeleteAccount(false)}
      />
    </div>
  );
}

export default Account;
